import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { unitId } from '@/annotations'
import { Avatar } from '@/components/Avatar'
import { Button } from '@/components/Button'
import { Chip } from '@/components/Chip'
import { IconButton } from '@/components/IconButton'
import { Note } from '@/components/Note'
import { Pager } from '@/components/Pager'
import { GridTable } from '@/components/GridTable'
import { MCancel } from '@/screens/m-cancel/MCancel'
import { JOB_STATUS_CHIP } from '@/screens/shared/jobFormatting'
import { useCursorPager } from '@/data/useCursorPager'
import { getApiClient, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type BackupJob = components['schemas']['BackupJob']
type JobStatus = components['schemas']['JobStatus']

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Pending',
  RUNNING: 'Running',
  SUCCEEDED: 'Succeeded',
  PARTIAL_SUCCESS: 'Partial success',
  FAILED: 'Failed',
  SKIPPED: 'Skipped',
  CANCELLED: 'Cancelled',
}

/**
 * `v-jobsbackup` — process/09-SCREEN-SPECS/v-jobsbackup.md. `Export CSV` has no operation at all
 * (`D89`) — rendered disabled, wired to nothing. A cancelled row is neutral, never a failure
 * (rule 2); a null `triggered_by` renders *System*, never a blank (`BackupJob` schema).
 *
 * **Two of the spec's own three `grid-tools` `icon-button`s added W157** — "Clear filters" (real:
 * resets the status filter already on this screen) and "Refresh" (real: invalidates the same query
 * the pager already reads). Neither carries a `data-act` — the Actions table names only `Cancel`.
 * The third, a column chooser, is not built: no column-visibility state exists anywhere to back one.
 *
 * **`avatar` added W161** — beside `triggered_by.display_name`, shown only when a real person
 * triggered the job (a null `triggered_by` still renders *System*, with no avatar).
 *
 * **`grid-filter-row` added (W170)** — the status `<select>` moved out of
 * `grid-tools` into its own real `grid-filter-row`, matching the spec's own "in `grid-table`"
 * placement; no new content, the same already-real filter state W157 already wired "Clear filters"
 * against.
 *
 * **The status chip now uses `JOB_STATUS_CHIP` (W172)** — see `jobFormatting.ts`'s own doc comment
 * for the prototype evidence; was `variant={status === 'FAILED' ? 'default' : 'plain'}`, flat blue
 * or flat grey regardless of the real outcome.
 */
export function VJobsbackup() {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const pager = useCursorPager()
  const [status, setStatus] = useState<JobStatus | ''>('')
  const [cancelling, setCancelling] = useState<BackupJob | undefined>()

  const list = useQuery({
    queryKey: ['backup-jobs', orgId, workspaceId, pager.cursor, status],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — see that
    // fix's own doc comment for the mount-time/org-switch race this guards against.
    enabled: Boolean(orgId && workspaceId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/backups', {
        params: { path: { org_id: orgId, workspace_id: workspaceId }, query: { cursor: pager.cursor, status: status || undefined } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
  })

  const problem = list.error instanceof ApiProblemError ? list.error.problem : undefined
  const rows = (list.data?.items ?? []) as unknown as readonly BackupJob[]

  return (
    <div data-scr={unitId('v-jobsbackup')}>
      <div data-cmp="panel-header" className="flex items-center gap-5 mb-6">
        <h1 className="m-0 text-section font-semibold flex-1">Jobs: backup</h1>
        <div data-cmp="grid-tools" className="flex gap-4 items-center">
          <IconButton
            data-act={undefined}
            icon="i-filter-off"
            label="Clear filters"
            disabled={!status}
            onClick={() => {
              pager.reset()
              setStatus('')
            }}
          />
          <IconButton
            data-act={undefined}
            icon="i-refresh"
            label="Refresh"
            disabled={list.isFetching}
            onClick={() => queryClient.invalidateQueries({ queryKey: ['backup-jobs', orgId, workspaceId] })}
          />
          <Button data-act={undefined} ghost disabled title="No export exists for this list (D89)">
            Export CSV
          </Button>
        </div>
      </div>

      {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Note>{problem.title}</Note>)}

      <div data-cmp="grid-filter-row" className="mb-4">
        <select
          value={status}
          onChange={(e) => {
            pager.reset()
            setStatus(e.target.value as JobStatus | '')
          }}
          className="border border-bd2 rounded-1 py-2 px-3 text-small"
        >
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-2">
      <GridTable
        onRowClick={(job) => navigate(`/jobs/backup/${job.job.job_id}`)}
        columns={[
          { key: 'job', header: 'Job', render: (job) => <span className="font-mono">{job.job.handle}</span> },
          { key: 'kind', header: 'Kind', render: (job) => (job.backup_kind === 'CONFIG_BACKUP' ? 'Configuration' : 'Data') },
          { key: 'tag', header: 'Tag', render: (job) => job.tag ?? '—' },
          {
            key: 'status',
            header: 'Status',
            render: (job) => (
              <>
                <Chip variant={JOB_STATUS_CHIP[job.job.status] ?? 'plain'}>{STATUS_LABEL[job.job.status] ?? job.job.status}</Chip>
                {job.error_category && <Note>{job.error_category}</Note>}
                {job.skip_reason && <Note>{job.skip_reason}</Note>}
                {job.cancellation && <Note>cancelled by {job.cancellation.requested_by.display_name ?? 'unknown'}{job.cancellation.reason ? ` — ${job.cancellation.reason}` : ''}</Note>}
              </>
            ),
          },
          { key: 'started', header: 'Started', render: (job) => (job.started_at ? job.started_at.display : '—') },
          { key: 'duration', header: 'Duration', render: (job) => (job.duration ? job.duration.display : '—') },
          {
            key: 'triggeredBy',
            header: 'Triggered by',
            render: (job) =>
              job.triggered_by?.display_name ? (
                <>
                  <Avatar name={job.triggered_by.display_name} />
                  {job.triggered_by.display_name}
                </>
              ) : (
                'System'
              ),
          },
          {
            key: 'actions',
            header: '',
            stopRowClick: true,
            render: (job) =>
              job.job.status === 'RUNNING' || job.job.status === 'PENDING' ? (
                job.cancellation ? (
                  <Note>Cancelling…</Note>
                ) : (
                  <Button data-act={undefined} ghost onClick={() => setCancelling(job)}>
                    Cancel
                  </Button>
                )
              ) : null,
          },
        ]}
        rows={rows}
        getRowKey={(job) => job.job.job_id}
        emptyMessage={list.isLoading ? '' : 'No jobs match these filters.'}
      />
      </div>

      <div data-cmp="grid-footer" className="border-t border-bd flex items-center justify-between mt-5">
        <Note>Paged by cursor only — no total is served for this list.</Note>
        <Pager
          hasPrev={pager.hasPrev}
          hasNext={Boolean(list.data?.next_cursor)}
          loading={list.isFetching}
          onPrev={pager.goPrev}
          onNext={() => {
            if (list.data?.next_cursor) pager.goNext(list.data.next_cursor)
          }}
        />
      </div>

      {cancelling && (
        <MCancel
          orgId={orgId}
          workspaceId={workspaceId}
          job={cancelling.job}
          progress={cancelling.repositories}
          onDismiss={() => setCancelling(undefined)}
          onCancelled={() => {
            setCancelling(undefined)
            queryClient.invalidateQueries({ queryKey: ['backup-jobs', orgId, workspaceId] })
          }}
        />
      )}
    </div>
  )
}
